/**
 * Pure rules of "My approvals" (19 Oct): the due badge, the order, and where
 * a link to one case goes. No React here, so `node --test` reads it directly.
 */

/** The parts of a need these rules read (a subset of `AccessApprovalNeed`). */
export interface DueNeed {
  due_at?: string | null;
  overdue?: boolean | null;
  reminders_sent?: number | null;
}

/** The parts of a card these rules read (a subset of `MyApproval`). */
export interface FocusCard {
  case_id: string;
  need: DueNeed & { waiting_since?: string | null };
}

export type DueState = "overdue" | "dueSoon" | "due" | null;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * "overdue" once the server says so (the last reminder's period passed);
 * "dueSoon" within a day of the due date, or past it; "due" before that;
 * null when there is no due date (reminders off).
 */
export function dueState(need: DueNeed, now: number = Date.now()): DueState {
  if (need.overdue === true) {
    return "overdue";
  }
  if (!need.due_at) {
    return null;
  }
  const due = Date.parse(need.due_at);
  if (Number.isNaN(due)) {
    return null;
  }
  return due - now <= DAY_MS ? "dueSoon" : "due";
}

/** The design system tone of a due badge. */
export function dueTone(state: DueState): "danger" | "warning" | "neutral" {
  return state === "overdue" ? "danger" : state === "dueSoon" ? "warning" : "neutral";
}

/** The case the link named first, then as the server ordered them (overdue, oldest). */
export function focusFirst<C extends FocusCard>(cards: readonly C[], caseId: string | null): C[] {
  if (!caseId) {
    return [...cards];
  }
  return [
    ...cards.filter((c) => c.case_id === caseId),
    ...cards.filter((c) => c.case_id !== caseId),
  ];
}

/**
 * Where a link to one case lands (19 Oct): the approval email links to
 * `/approvals?case=<id>`. Something there waits for the reader, or they just
 * decided it (the card moved to Decided): stay. Neither (someone else opened
 * the link, or an IT colleague followed the case link): the case page.
 */
export function landing(
  caseId: string | null,
  waiting: readonly FocusCard[],
  decided: readonly FocusCard[] = [],
): { kind: "stay" } | { kind: "case"; path: string } {
  if (!caseId || [...waiting, ...decided].some((c) => c.case_id === caseId)) {
    return { kind: "stay" };
  }
  return { kind: "case", path: `/access/${encodeURIComponent(caseId)}` };
}

/** The parts of `MyApprovalsResponse` the header reads. */
export interface ApprovalsNavSource {
  waiting?: readonly unknown[] | null;
  decided?: readonly unknown[] | null;
  test_approver?: boolean | null;
  approver?: boolean | null;
}

/** "My approvals" in the header (19 Oct): whether to show it, and its badge. */
export interface ApprovalsNav {
  show: boolean;
  waiting: number;
}

/**
 * Shown to whoever has, or has had, an approval, and to anyone the approver
 * matrix names (or a test approver), so an ordinary user never meets an
 * empty page. Hidden until the answer is in (`undefined`).
 */
export function approvalsNav(data: ApprovalsNavSource | undefined | null): ApprovalsNav {
  if (data == null) {
    return { show: false, waiting: 0 };
  }
  const waiting = data.waiting?.length ?? 0;
  const decided = data.decided?.length ?? 0;
  const show = waiting > 0 || decided > 0 || data.approver === true || data.test_approver === true;
  return { show, waiting };
}
